import React, { useEffect, useState } from 'react';
import { Layout, Loading, Dropdown, Table, Button, Input, MessageBox, Notification, Select, Tooltip } from 'element-react';
import { useHistory } from "react-router-dom";
import CloneIcon from '../components/CloneIcon';
import DeleteIcon from '../components/DeleteIcon';
import { Auth, API } from 'aws-amplify';
import { GET_SIGNED_URL, IS_DEV, RFQ_API_URL } from '../utils/Constants'; 
import menuStore from '../store/menu-selected';
import rfqStore from '../store/rfqStore';

function RFQDirect(props) {
    const [loading, setLoading] = useState(false);
    const [rfqs, setRfqs] = useState([]);
    const [originalRfqs, setOriginalRfqs] = useState([]);
    const [filterTable, setFilterTable] = useState('');
    const [debouncedFilter, setDebouncedFilter] = useState('');
    const history = useHistory();
    const { setMenuIndex } = menuStore();

    // Cargar los datos cuando el componente se monta o cuando cambia el filtro de búsqueda
    useEffect(() => {
        loadRFQs();
    }, []);

    useEffect(() => {
        // Establece un temporizador que se ejecutará después de 300ms
        const timerId = setTimeout(() => {
            setDebouncedFilter(filterTable);
        }, 300); // 300ms de espera

        // Si el usuario sigue escribiendo, limpia el temporizador anterior
        return () => {
            clearTimeout(timerId);
        };
    }, [filterTable]);

    useEffect(() => {
        if (debouncedFilter) {
            const filteredData = originalRfqs.filter(item =>
                item.productDescription.toLowerCase().includes(debouncedFilter.toLowerCase())
            );
            setRfqs(filteredData);
        } else {
            // Si el buscador está vacío, muestra la lista original completa
            setRfqs(originalRfqs);
        }
    }, [debouncedFilter, originalRfqs]);

    // Función para obtener la lista de RFQs del backend
    const loadRFQs = async () => {
        setLoading(true);

        if (IS_DEV) {
            console.log("MODO DESARROLLO: Cargando RFQs locales desde el store.");

            const { mockRfqs } = rfqStore.getState();

            const formattedData = mockRfqs.map(item => ({
                id: item.id,
                fileKey: null, 
                creationDate: new Date(item.created_date).toLocaleDateString('en-CA'),
                productDescription: item.productName,
                quantity: item.quantity,
                unit: item.unit,
                requiredByDate: item.requiredDate ? new Date(item.requiredDate).toLocaleDateString('en-CA') : '-',
                status: 1,
                quotes: 0,
                
            }));
            
            setOriginalRfqs(formattedData);
            setRfqs(formattedData);
            setLoading(false);
            return;
        }
        // --- CÓDIGO ORIGINAL PARA PRODUCCIÓN ---
        try {
            const user = await Auth.currentAuthenticatedUser();
            const userEmail = user.attributes.email;
            
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();

            const response = await fetch(`${RFQ_API_URL}/getRFQByUser/${userEmail}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token
                },
            });
            

            if (!response.ok) {
                throw new Error('API request failed');
            }

            const data = await response.json();
            const result = data.rfq; 
            console.log("Datos directos del servidor:", result);

            const formattedData = result.map(item => {
                const statusObj = statusOptions.find(opt => opt.label === item.status);

                return {
                    id: item.id,
                    fileKey: item.file_key,
                    creationDate: new Date(item.created_date).toLocaleDateString('en-CA'),
                    productDescription: item.product_name,
                    quantity: item.quantity,
                    unit: item.unit,
                    requiredByDate: item.required_by_date ? new Date(item.required_by_date).toLocaleDateString('en-CA') : '-',
                    status: statusObj ? statusObj.value : 1,
                    quotes: item.total_proposals || 0,
                    category: [item.category_id, item.category_name],
                    requirements: item.product_description,
                    destination: item.destination,
                    shippingMethod: item.shipping_method,
                    incoterm: item.incoterm,
                    buyingFrequency: item.buying_frequency,
                    expiredIn: item.expire_in,
                };
            });
            
            setOriginalRfqs(formattedData);
            setRfqs(formattedData);

        } catch (error) {
            console.error("Error cargando RFQs:", error);
        } finally {
            setLoading(false);
        }
    };

    // Función para manejar la descarga de archivos
    const handleDownload = async (fileKey) => {
        if (!fileKey) return;

        setLoading(true);
        try {
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();

            const response = await fetch(`${GET_SIGNED_URL}?fileName=${fileKey}`, {
                headers: { 'Authorization': token }
            });

            if (!response.ok) throw new Error('Failed to get download link.');

            const data = await response.json();
            window.open(data.url, '_blank');

        } catch (error) {
            console.error("Error getting signed URL:", error);
            Notification.error('Could not retrieve file for download.');
        } finally {
            setLoading(false);
        }
    };

    // Función para manejar el borrado de un RFQ
    const deleteRfqHandler = async (id) => {
        try {
            await MessageBox.confirm('Are you sure you want to delete this RFQ?', 'Confirmation', { 
                type: 'warning' 
            });
            
            setLoading(true);

            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();

            const response = await fetch(`${RFQ_API_URL}/updateRfqStatusById`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token
                },
                body: JSON.stringify({
                    rfqId: id,
                    status: 4 // status para "borrado"
                })
            });

            if (!response.ok) {
                throw new Error('API request to delete failed');
            }

            const updatedList = originalRfqs.filter(rfq => rfq.id !== id);
            setOriginalRfqs(updatedList);
            setRfqs(updatedList); // actualiza la tabla

            Notification.success('RFQ deleted successfully!');

        } catch (error) {
            
            if (error !== 'cancel') { 
                console.error("Error deleting RFQ:", error);
                Notification.error('Failed to delete RFQ.');
            }
        } finally {
            setLoading(false);
        }
    };

    const cloneRfqHandler = (id) => {
        const rfqToCopy = originalRfqs.find(rfq => rfq.id === id);

        if (rfqToCopy) {
            console.log("Copiando los siguientes datos al formulario:", rfqToCopy);
            
            history.push({
                pathname: '/tools/rfq', 
                state: { rfqData: rfqToCopy } 
            });
        } else {
            console.error("No se pudo encontrar el RFQ para copiar.");
            Notification.error('Could not find the RFQ to copy.');
        }
    };

    const goToCreateRFQ = () => {
        setMenuIndex('/tools/rfq'); 
        // Navegamos a la página
        history.push("/tools/rfq");
    };

    // FUNCIÓN para el cambio de status 
    const handleStatusChange = async (rfqId, newStatus) => {
        setLoading(true);
        try {
            
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();

            const response = await fetch(`${RFQ_API_URL}/updateRfqStatusById`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token 
                },
                body: JSON.stringify({
                    rfqId: rfqId,
                    status: newStatus
                })
            });

            if (!response.ok) {
                throw new Error('API request failed');
            }

            const data = await response.json();
            console.log("Respuesta de updateRfqStatusById:", data);

            const updateRfqs = (list) => list.map(rfq =>
                rfq.id === rfqId ? { ...rfq, status: newStatus } : rfq
            );
            
            setOriginalRfqs(prev => updateRfqs(prev));
            setRfqs(prev => updateRfqs(prev));

            Notification.success('Status updated successfully!');
        } catch (error) {
            console.error("Error updating status:", error);
            Notification.error('Failed to update status.');
        } finally {
            setLoading(false);
        }
    };

    const statusOptions = [
        { value: 1, label: 'Active' },
        { value: 0, label: 'Paused' },
        { value: 2, label: 'Archived' }
    ];

    // columnas para la tabla
    const columns = [
        { label: 'Date', prop: 'creationDate', width: 110, sortable: true },
        { label: 'Id', prop: 'id', width: 60, sortable: true,
            render: (row) => (
                <span>{`R${row.id}`}</span>
            )
        },
        {
            label: 'Product',
            prop: 'productDescription',
            render: (row) => (
                <Tooltip content={row.productDescription} placement="bottom-start">
                    <span className="product-name truncate-text">{row.productDescription}</span>
                </Tooltip>
            )
        },
        { label: 'Quantity', prop: 'quantity', width: 90 },
        { label: 'Unit', prop: 'unit', width: 90 },
        {
            label: 'Attachment',
            width: 110,
            render: (row) => (
                row.fileKey 
                    ? <Button type="text" onClick={() => handleDownload(row.fileKey)}>Download</Button> 
                    : '–'
            )
        },
        { label: 'Required By', prop: 'requiredByDate', width: 145, sortable: true },
        {
            label: 'Status',
            width: 115, 
            render: (row) => (
                <div onClick={(e) => e.stopPropagation()}>
                    <Select value={row.status} onChange={(newStatus) => handleStatusChange(row.id, newStatus)}>
                        {
                            statusOptions.map(el => {
                                return <Select.Option key={el.value} label={el.label} value={el.value} />
                            })
                        }
                    </Select>
                </div>
            )
        },
        {
            label: 'Quotes',
            prop: 'quotes', 
            width: 80,
            align: 'center'
        },
        {
            label: 'Actions',
            width: 90,
            align: 'center',
            render: (row) => (
                <div className="action-icons" onClick={(e) => e.stopPropagation()}>
                    <Tooltip content="Delete" placement="bottom">
                        <DeleteIcon handleClick={() => deleteRfqHandler(row.id)} width={35} />
                    </Tooltip>
                    <Tooltip content="Clone" placement="bottom">
                        <CloneIcon handleClick={() => cloneRfqHandler(row.id)} width={35}/>
                    </Tooltip>
                </div>
            )
        }
    ];

    return (
        <div className="RFQDirect">
            {loading && <Loading fullscreen={loading} />}
            
            <div className="page-header">
                <div className="header-content">
                    <h1>
                        <span className="title-red">RFQ</span>
                        <span className="title-dark">Direct</span>
                    </h1>
                    <h2 className="subtitle" style={{ fontWeight: '600', color: '#343537ff' }}>
                        My Requests For Quotations
                    </h2>               
                </div>
                <div className="controls-row">
                    <div className="actions-group">
                        <Input
                            icon="search"
                            placeholder="Search"
                            value={filterTable}
                            onChange={setFilterTable}
                            className='input-responsiveRFQ'
                        />
                        <Button type="primary" onClick={goToCreateRFQ}>
                            Create RFQ
                        </Button>
                    </div>
                </div>
            </div>
            <div>
                <p style={{ marginTop: '0px', fontSize: '14px' }}>
                        These are your RFQs. Select any item to access complete details and evaluate the quotation proposals provided by suppliers.
                    </p>
            </div>
            
            <div className="table-container responsive-table-container">
                <Table
                    className="rfq-table clickable-rows"
                    data={rfqs}
                    columns={columns}
                    fit={true} // evita el scroll horizontal
                    emptyText="No RFQs found."
                    onRowClick={(row) => {
                        console.log('Navegando a los detalles del RFQ:', row.id);
                        history.push(`/tools/rfq-details/${row.id}`);
                    }}
                />
            </div>
        </div>
    );
}

export default RFQDirect;